import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, Loader2, Check } from "lucide-react";
import { MailAttachment } from "./types";

interface EmailResumeParserProps {
  attachment: MailAttachment;
  emailFromName?: string | null;
  emailFromEmail?: string;
}

export function EmailResumeParser({
  attachment,
  emailFromName,
  emailFromEmail,
}: EmailResumeParserProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const { toast } = useToast();
  const { user } = useAuth();

  const isResume = /\.(pdf|docx?|doc)$/i.test(attachment.file_name);

  if (!isResume) return null;

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const { data, error } = await supabase
        .from("jobs")
        .select("id, title, clients(name)")
        .in("status", ["draft", "published"])
        .order("created_at", { ascending: false });

      if (error) console.error("Error fetching jobs:", error);
      setJobs(data || []);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setSuccess(false);
    fetchJobs();
  };

  const createCandidateFromAttachment = async () => {
    setLoading(true);

    try {
      // Check if file was actually stored (file_size > 0 indicates successful storage)
      if (!attachment.file_size || attachment.file_size === 0) {
        throw new Error(
          "Attachment file was not saved to storage. The email may have been received before the attachment processing was fixed. Please ask the sender to resend the email."
        );
      }

      // Download the attachment from storage
      const { data: fileData, error: downloadError } = await supabase.storage
        .from("inbound-email-attachments")
        .download(attachment.storage_path);

      if (downloadError) {
        throw new Error(`Could not download attachment: ${downloadError.message}`);
      }

      // Parse name from email sender or filename
      let firstName = "Unknown";
      let lastName = "Candidate";

      if (emailFromName) {
        const nameParts = emailFromName.split(/\s+/);
        firstName = nameParts[0] || firstName;
        lastName = nameParts.slice(1).join(" ") || lastName;
      } else {
        const fileNameParts = attachment.file_name
          .replace(/\.(pdf|docx?)$/i, "")
          .split(/[_\-\s]+/);
        firstName = fileNameParts[0] || firstName;
        lastName = fileNameParts.slice(1).join(" ") || lastName;
      }

      // Get current user's app_users id
      const { data: appUser } = await supabase
        .from("app_users")
        .select("id")
        .eq("user_id", user?.id)
        .single();

      // Create candidate record
      const { data: candidate, error: candidateError } = await supabase
        .from("candidates")
        .insert({
          first_name: firstName,
          last_name: lastName,
          email_primary: emailFromEmail || null,
        })
        .select()
        .single();

      if (candidateError) throw candidateError;

      // Upload file to resumes storage bucket
      const storagePath = `${candidate.id}/${Date.now()}_${attachment.file_name}`;
      const { error: uploadError } = await supabase.storage
        .from("resumes")
        .upload(storagePath, fileData);

      if (uploadError) throw uploadError;

      // Create resume record
      const { data: resumeRecord, error: resumeError } = await supabase
        .from("candidate_resumes")
        .insert({
          candidate_id: candidate.id,
          original_filename: attachment.file_name,
          storage_path: storagePath,
          file_size: attachment.file_size,
        })
        .select()
        .single();

      if (resumeError) throw resumeError;

      // Create candidate application if job is selected
      if (selectedJobId) {
        await supabase.from("candidate_applications").insert({
          candidate_id: candidate.id,
          job_id: selectedJobId,
          status: "applied",
        });
      }

      // Trigger deep parsing
      supabase.functions
        .invoke("trigger-deep-parse", {
          body: {
            candidateId: candidate.id,
            resumeId: resumeRecord.id,
            fileName: attachment.file_name,
            jobId: selectedJobId || null,
            uploadedBy: appUser?.id,
          },
        })
        .catch((err) => console.error("Failed to trigger parsing:", err));

      setSuccess(true);
      toast({
        title: "Candidate created",
        description: `${firstName} ${lastName} has been created and resume is being parsed.`,
      });

      setTimeout(() => {
        setOpen(false);
        setSuccess(false);
      }, 1500);
    } catch (error: any) {
      console.error("Error creating candidate:", error);
      toast({
        title: "Failed to create candidate",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        className="h-auto py-1 px-2"
        onClick={handleOpen}
      >
        <UserPlus className="h-3 w-3 mr-1" />
        Create Candidate
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Candidate from Resume</DialogTitle>
            <DialogDescription>
              Create a new candidate profile from this resume attachment. The
              resume will be parsed automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">Resume file:</p>
              <p className="font-medium">{attachment.file_name}</p>
            </div>

            {emailFromName && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  Sender name:
                </p>
                <p className="font-medium">{emailFromName}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">
                Link to Job (optional)
              </label>
              <Select
                value={selectedJobId}
                onValueChange={(val) => setSelectedJobId(val === "none" ? "" : val)}
                disabled={loadingJobs}
              >
                <SelectTrigger>
                  {loadingJobs ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading jobs...
                    </span>
                  ) : (
                    <SelectValue placeholder="Select a job (optional)" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No job selected</SelectItem>
                  {jobs.filter(job => job.id).map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title} {job.clients?.name && `- ${job.clients.name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={createCandidateFromAttachment}
                disabled={loading || success}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : success ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Created!
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Candidate
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
